<template>
  <aside class="sidebar" :class="{ 'sidebar--open': open }">
    <!-- Brand -->
    <div class="sidebar-brand">
      <div class="brand-logo">
        <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse
            cx="18"
            cy="9.5"
            rx="3.2"
            ry="7"
            fill="#d4a0a0"
            opacity="0.9"
          />
          <ellipse
            cx="18"
            cy="9.5"
            rx="3.2"
            ry="7"
            fill="#d4a0a0"
            opacity="0.88"
            transform="rotate(72 18 18)"
          />
          <ellipse
            cx="18"
            cy="9.5"
            rx="3.2"
            ry="7"
            fill="#e0b0b0"
            opacity="0.82"
            transform="rotate(144 18 18)"
          />
          <ellipse
            cx="18"
            cy="9.5"
            rx="3.2"
            ry="7"
            fill="#d4a0a0"
            opacity="0.88"
            transform="rotate(216 18 18)"
          />
          <ellipse
            cx="18"
            cy="9.5"
            rx="3.2"
            ry="7"
            fill="#e0b0b0"
            opacity="0.82"
            transform="rotate(288 18 18)"
          />
          <circle cx="18" cy="18" r="5" fill="#c9956c" />
          <circle cx="18" cy="18" r="3" fill="#e8c4a0" />
          <circle cx="18" cy="18" r="1.4" fill="#f5dfc0" opacity="0.85" />
        </svg>
      </div>
      <div class="brand-text">
        <span class="brand-name">{{ companyName }}</span>
        <span class="brand-sub">Gestão</span>
      </div>
    </div>

    <!-- Company switcher -->
    <div
      v-if="companies.length > 1"
      v-click-outside="closeDropdown"
      class="company-switcher"
    >
      <button class="switcher-btn" @click="toggleDropdown">
        <div
          class="switcher-avatar"
          :style="{
            background: resolveLogoUrl(currentCompany)
              ? '#fff'
              : currentCompany?.accent_color || 'var(--c-accent)',
          }"
        >
          <img
            v-if="resolveLogoUrl(currentCompany)"
            :src="resolveLogoUrl(currentCompany)"
            :alt="currentCompany.nome"
          />
          <span v-else>{{ companyName.charAt(0) }}</span>
        </div>
        <div class="switcher-info">
          <span class="switcher-name">{{ companyName }}</span>
          <span class="switcher-label">Trocar empresa</span>
        </div>
        <svg
          class="switcher-chevron"
          :class="{ 'switcher-chevron--open': dropdownOpen }"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div v-if="dropdownOpen" class="switcher-dropdown">
        <div class="switcher-dropdown__label">Suas empresas</div>
        <button
          v-for="company in companies"
          :key="company.id"
          class="switcher-option"
          :class="{
            'switcher-option--active': company.id === currentCompany?.id,
          }"
          @click="switchCompany(company)"
        >
          <div
            class="option-avatar"
            :style="{
              background: resolveLogoUrl(company)
                ? '#fff'
                : company.accent_color || 'var(--c-accent)',
            }"
          >
            <img
              v-if="resolveLogoUrl(company)"
              :src="resolveLogoUrl(company)"
              :alt="company.nome"
            />
            <span v-else>{{ company.nome.charAt(0) }}</span>
          </div>
          <div class="option-info">
            <span class="option-name">{{ company.nome }}</span>
            <span class="option-slogan">{{ company.slogan }}</span>
          </div>
          <svg
            v-if="company.id === currentCompany?.id"
            class="option-check"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav" aria-label="Navegação principal">
      <div class="nav-section">Vendas</div>

      <router-link
        to="/pedidos"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/pedidos') ? 'page' : undefined"
      >
        <AppIcon
          name="prancheta"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <span>Pedidos</span>
      </router-link>

      <router-link
        to="/galeria"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/galeria') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
          />
        </svg>
        <span>Galeria</span>
      </router-link>

      <router-link
        to="/expedicao"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/expedicao') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"
          />
        </svg>
        <span>Expedição</span>
      </router-link>

      <router-link
        to="/agenda"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/agenda') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
          />
        </svg>
        <span>Agenda</span>
      </router-link>

      <router-link
        to="/clientes"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/clientes') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-4.5 0 2.625 2.625 0 014.5 0z"
          />
        </svg>
        <span>Clientes</span>
      </router-link>

      <router-link
        to="/relatorios"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/relatorios') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
          />
        </svg>
        <span>Relatórios</span>
      </router-link>

      <router-link
        to="/financeiro"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/financeiro') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>Financeiro</span>
      </router-link>

      <router-link
        to="/calculadora"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/calculadora') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm3-6h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm3-6h.008v.008H14.25v-.008zm0 3h.008v.008H14.25v-.008zm6-10.125c0-.621-.504-1.125-1.125-1.125H2.625A1.125 1.125 0 001.5 6.375v11.25C1.5 18.504 2.004 19 2.625 19h16.5c.621 0 1.125-.504 1.125-1.125V6.375z"
          />
        </svg>
        <span>Calculadora</span>
      </router-link>

      <div class="nav-section">Estoque</div>

      <router-link
        to="/produtos"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/produtos') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
          />
        </svg>
        <span>Produtos</span>
      </router-link>

      <router-link
        to="/estoque"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/estoque') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5"
          />
        </svg>
        <span>Estoque</span>
        <span
          v-if="alertasEstoqueCount > 0"
          class="nav-badge nav-badge--danger"
        >
          {{ alertasEstoqueCount }}
        </span>
      </router-link>

      <router-link
        to="/materiais"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/materiais') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3"
          />
        </svg>
        <span>Materiais</span>
      </router-link>

      <router-link
        to="/requisicoes"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/requisicoes') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
        <span>Requisições</span>
        <span
          v-if="requisicoesPendentesCount > 0"
          class="nav-badge nav-badge--warning"
        >
          {{ requisicoesPendentesCount }}
        </span>
      </router-link>

      <router-link
        to="/fornecedores"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/fornecedores') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
          />
        </svg>
        <span>Fornecedores</span>
      </router-link>

      <div class="nav-section">Configurações</div>

      <router-link
        to="/colaboradores"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/colaboradores') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
          />
        </svg>
        <span>Responsáveis</span>
      </router-link>

      <router-link
        to="/tipos-materiais"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/tipos-materiais') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3zM6 6h.008v.008H6V6z"
          />
        </svg>
        <span>Tipos de Materiais</span>
      </router-link>

      <router-link
        to="/cores"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/cores') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z"
          />
        </svg>
        <span>Cores</span>
      </router-link>

      <router-link
        to="/fretes"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="$route.path.startsWith('/fretes') ? 'page' : undefined"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
          />
        </svg>
        <span>Fretes</span>
      </router-link>

      <router-link
        to="/configuracao-empresa"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/configuracao-empresa') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z"
          />
          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span>Empresa</span>
      </router-link>

      <router-link
        v-if="podeConfigurarNotificacoes"
        to="/notificacoes"
        class="nav-item"
        active-class="nav-item--active"
        :aria-current="
          $route.path.startsWith('/notificacoes') ? 'page' : undefined
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          />
        </svg>
        <span>Notificações</span>
      </router-link>

      <!-- Visível apenas para super admin -->
      <template v-if="isSuperAdmin">
        <div class="nav-section nav-section--root">Sistema</div>
        <router-link
          to="/sistema"
          class="nav-item nav-item--root"
          active-class="nav-item--active"
          :aria-current="
            $route.path.startsWith('/sistema') ? 'page' : undefined
          "
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25"
            />
          </svg>
          <span>Gestão do Sistema</span>
        </router-link>
      </template>
    </nav>

    <!-- Sidebar footer -->
    <div class="sidebar-footer">
      <div v-if="instagramUrl || whatsappUrl" class="sidebar-contact">
        <a
          v-if="instagramUrl"
          :href="instagramUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="contact-link"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
          </svg>
          <span>{{ currentCompany.instagram }}</span>
        </a>
        <a
          v-if="whatsappUrl"
          :href="whatsappUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="contact-link"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
            />
          </svg>
          <span>{{ currentCompany.whatsapp }}</span>
        </a>
      </div>
      <!-- No browser: link para instalar pela primeira vez -->
      <a
        v-if="!isNativeApp"
        :href="apkDownloadUrl"
        download="fina-mabe-erp.apk"
        class="btn-download-apk"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <path d="M12 8v8M9 13l3 3 3-3" />
        </svg>
        <span>Baixar app Android</span>
      </a>

      <!-- No app nativo: botão de download somente quando há atualização disponível -->
      <template v-if="isNativeApp && updateAvailable">
        <button
          class="btn-download-apk btn-download-apk--update"
          @click="iniciarDownloadApk"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
            />
          </svg>
          <span>Baixar atualização</span>
        </button>
      </template>

      <button class="btn-logout" @click="logout">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
          />
        </svg>
        <span>Sair</span>
      </button>
    </div>
  </aside>
</template>

<script>
import { sair } from "@/services/sessao";
import { isNativeApp } from "@/utils/platform";
import { APK_DOWNLOAD_URL } from "@/config/appVersion";
import { resolveLogoUrl as resolveLogoUrlHelper } from "@/services/logoUrl";
import produtosService from "@/services/produtos";
import requisicoesService from "@/services/requisicoes";

export default {
  name: "AppSidebar",
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) binding.value();
        };
        document.addEventListener("mousedown", el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener("mousedown", el._clickOutside);
      },
    },
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownOpen: false,
      alertasEstoqueCount: 0,
      requisicoesPendentesCount: 0,
    };
  },
  computed: {
    currentCompany() {
      return this.$store.getters.currentCompany;
    },
    companies() {
      return this.$store.getters.companies;
    },
    companyName() {
      return this.currentCompany?.nome || "ERP";
    },
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin;
    },
    // Notificações: só admin da empresa (a API também só aceita admin)
    podeConfigurarNotificacoes() {
      return this.isSuperAdmin || this.$store.getters.currentRole === "admin";
    },
    instagramUrl() {
      const val = this.currentCompany?.instagram;
      if (!val) return null;
      if (val.includes("instagram.com/")) {
        return val.startsWith("http") ? val : `https://${val}`;
      }
      return `https://instagram.com/${val.replace(/^@/, "")}`;
    },
    isNativeApp() {
      return isNativeApp();
    },
    apkDownloadUrl() {
      return APK_DOWNLOAD_URL;
    },
    updateAvailable() {
      return this.$store.state.updateAvailable;
    },
    updateApkUrl() {
      return this.$store.state.updateApkUrl || APK_DOWNLOAD_URL;
    },
    whatsappUrl() {
      const num = this.currentCompany?.whatsapp;
      if (!num) return null;
      const digits = num.replace(/\D/g, "");
      const full = digits.startsWith("55") ? digits : `55${digits}`;
      return `https://wa.me/${full}`;
    },
  },
  watch: {
    currentCompany() {
      this.carregarContadores();
    },
  },
  created() {
    this.carregarContadores();
  },
  methods: {
    async carregarContadores() {
      if (
        !this.$store.getters.isAuthenticated ||
        !this.$store.getters.hasCompanySelected
      )
        return;
      try {
        const [alertas, requisicoes] = await Promise.allSettled([
          produtosService.alertasEstoque(),
          requisicoesService.getAll(),
        ]);
        if (alertas.status === "fulfilled") {
          const d = alertas.value.data;
          this.alertasEstoqueCount =
            (d.produtos?.length || 0) +
            (d.materiais?.length || 0) +
            (d.variantes?.length || 0);
        }
        if (requisicoes.status === "fulfilled") {
          const lista = Array.isArray(requisicoes.value.data)
            ? requisicoes.value.data
            : [];
          this.requisicoesPendentesCount = lista.filter(
            (r) => r.status === "pendente"
          ).length;
        }
      } catch {
        // silencioso — badges ficam zerados
      }
    },
    resolveLogoUrl(company) {
      if (!company) return null;
      if (company.logo_url) return resolveLogoUrlHelper(company.logo_url);
      const slug = (company.slug || company.nome || "").toLowerCase();
      if (slug.includes("mabe")) return "/logos/mabe.jpg";
      if (slug.includes("fina")) return "/logos/fina-arte.jpg";
      return null;
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    switchCompany(company) {
      this.dropdownOpen = false;
      this.$store.commit("SET_COMPANY", company);
      this.$router.push("/pedidos");
    },
    iniciarDownloadApk() {
      window.open(this.updateApkUrl, "_system");
      // Snooze de 2 horas para não reaparecer enquanto o usuário instala
      const until = Date.now() + 2 * 60 * 60 * 1000;
      localStorage.setItem("update-snooze-until", String(until));
    },
    async logout() {
      await sair();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.sidebar {
  width: var(--sidebar-w);
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(
    180deg,
    var(--c-gradient-from) 0%,
    var(--c-gradient-from) 30%,
    var(--c-gradient-to) 100%
  );
  border-right: 1px solid var(--c-sidebar-border);
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 200;
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  box-shadow: 1px 0 0 var(--c-sidebar-border);
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.5rem 1.25rem 1.25rem;
  border-bottom: 1px solid var(--c-sidebar-border);
  background: linear-gradient(
    to bottom,
    var(--c-sidebar-active) 0%,
    transparent 100%
  );
}

.brand-logo svg {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 5px var(--c-sidebar-border));
}

.brand-logo ellipse {
  fill: var(--c-sidebar-accent);
}
.brand-logo circle:nth-of-type(1) {
  fill: var(--c-sidebar-accent);
}
.brand-logo circle:nth-of-type(2) {
  fill: var(--c-sidebar-accent);
  opacity: 0.55;
}
.brand-logo circle:nth-of-type(3) {
  fill: #fff;
  opacity: 0.6;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.brand-name {
  font-family: var(--font-brand);
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--c-sidebar-text);
  letter-spacing: 0.01em;
  text-shadow: 0 1px 8px var(--c-accent-glow);
}

.brand-sub {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--c-sidebar-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 1px;
}

.sidebar-nav {
  padding: 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.nav-section {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--c-sidebar-section);
  padding: 1rem 0.625rem 0.375rem;
  margin-top: 0.375rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6875rem 0.875rem;
  border-radius: var(--r-sm);
  color: var(--c-sidebar-text);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: var(--tr);
  position: relative;
  opacity: 0.85;
}

.nav-badge {
  margin-left: auto;
  min-width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.3rem;
  border-radius: var(--r-full);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}

.nav-badge--danger {
  background: #dc2626;
  color: #fff;
}

.nav-badge--warning {
  background: #d97706;
  color: #fff;
}

.nav-item svg {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
}

.nav-item:hover {
  background: var(--c-sidebar-hover);
  opacity: 1;
}

.nav-item--active {
  background: linear-gradient(
    90deg,
    rgba(201, 149, 108, 0.22) 0%,
    rgba(201, 149, 108, 0.08) 100%
  );
  color: #f5e8d8;
  font-weight: 600;
  opacity: 1;
}

.nav-item--active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 15%;
  bottom: 15%;
  width: 3px;
  background: linear-gradient(180deg, #d4a878 0%, #c9956c 50%, #a8784e 100%);
  border-radius: 0 var(--r-xs) var(--r-xs) 0;
  box-shadow: 0 0 10px rgba(201, 149, 108, 0.7),
    0 0 4px rgba(201, 149, 108, 0.5);
}

.sidebar-footer {
  padding: 1rem 0.75rem calc(1.25rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(201, 149, 108, 0.12);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.18) 0%, transparent 100%);
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.sidebar-contact {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.contact-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.625rem;
  border-radius: var(--r-xs);
  color: var(--c-sidebar-muted);
  font-size: 0.8rem;
  font-weight: 500;
  transition: var(--tr);
  text-decoration: none;
}

.contact-link svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.contact-link:hover {
  color: #f0e8dc;
  background: var(--c-sidebar-hover);
}

.btn-download-apk {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.875rem;
  background: rgba(201, 149, 108, 0.1);
  border: 1px solid rgba(201, 149, 108, 0.3);
  border-radius: var(--r-sm);
  color: rgba(201, 149, 108, 0.9);
  font-family: var(--font);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: var(--tr);
}

.btn-download-apk svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.btn-download-apk:hover {
  background: rgba(201, 149, 108, 0.2);
  color: #f0e0c8;
  border-color: rgba(201, 149, 108, 0.5);
}

.btn-download-apk--update {
  background: rgba(201, 149, 108, 0.18);
  border-color: rgba(201, 149, 108, 0.5);
  color: #f0d8b8;
  animation: pulse-update 2.5s ease-in-out infinite;
}

@keyframes pulse-update {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(201, 149, 108, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(201, 149, 108, 0.2);
  }
}

.btn-logout {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.875rem;
  background: none;
  border: 1px solid rgba(201, 149, 108, 0.25);
  border-radius: var(--r-sm);
  color: rgba(201, 149, 108, 0.8);
  font-family: var(--font);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--tr);
}

.btn-logout svg {
  width: 15px;
  height: 15px;
}

.btn-logout:hover {
  background: var(--c-sidebar-active);
  color: var(--c-sidebar-accent);
  border-color: var(--c-sidebar-border);
}

.nav-section--root {
  color: rgba(167, 139, 250, 0.7);
}

.nav-item--root {
  color: rgba(167, 139, 250, 0.85);
}

.nav-item--root:hover {
  background: rgba(124, 58, 237, 0.12);
  opacity: 1;
}

/* Company switcher */
.company-switcher {
  position: relative;
  padding: 0 0.75rem 0.5rem;
}

.switcher-btn {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  background: var(--c-sidebar-active, rgba(201, 149, 108, 0.1));
  border: 1px solid var(--c-sidebar-border, rgba(201, 149, 108, 0.2));
  border-radius: 10px;
  cursor: pointer;
  font-family: var(--font);
  transition: background 0.2s;
}

.switcher-btn:hover {
  background: var(--c-sidebar-active, rgba(201, 149, 108, 0.18));
}

.switcher-avatar {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 800;
  color: white;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.switcher-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}

.switcher-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
  min-width: 0;
}

.switcher-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--c-sidebar-text, rgba(255, 255, 255, 0.9));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.switcher-label {
  font-size: 0.6875rem;
  color: var(--c-sidebar-muted, rgba(255, 255, 255, 0.45));
}

.switcher-chevron {
  width: 14px;
  height: 14px;
  color: var(--c-sidebar-muted, rgba(255, 255, 255, 0.45));
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.switcher-chevron--open {
  transform: rotate(180deg);
}

.switcher-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0.75rem;
  right: 0.75rem;
  background: #1e1e2e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  overflow: hidden;
  z-index: 200;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
}

.switcher-dropdown__label {
  padding: 0.625rem 0.875rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.switcher-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font);
  transition: background 0.15s;
}

.switcher-option:hover {
  background: rgba(255, 255, 255, 0.07);
}

.switcher-option--active {
  background: rgba(255, 255, 255, 0.05);
}

.option-avatar {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 800;
  color: white;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.option-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}

.option-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
  min-width: 0;
}

.option-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.88);
}

.option-slogan {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.option-check {
  width: 15px;
  height: 15px;
  color: var(--c-accent);
  flex-shrink: 0;
}

@media (max-width: 1023px) {
  .sidebar {
    transform: translateX(calc(-1 * var(--sidebar-w)));
    box-shadow: var(--sh-xl);
  }

  .sidebar--open {
    transform: translateX(0);
  }
}
</style>
